import { Injectable } from '@angular/core';
import { IAccessDoorType, AccessDoorType } from '../models/access-door-type.model';
import { AuthService } from './auth.service';
import { HttpClient } from '@angular/common/http';
import { SpinnerService } from './spinner.service';
import { ToastService } from './toast.service';
import { environment } from '../../environments/environment';
import { IServerResponse, ServerResponse } from '../models/server-response.model';

@Injectable({
  providedIn: 'root'
})
export class AccessDoorTypeService {

  public loading: boolean = false;
  public accessDoorTypes: AccessDoorType[] = [];

  constructor(
    public authService: AuthService,
    private http: HttpClient,
    public spinnerService: SpinnerService,
    public toastService: ToastService
  ) {
    this.getAll();
  }

  /*
   ██████╗██████╗ ███████╗ █████╗ ████████╗███████╗
  ██╔════╝██╔══██╗██╔════╝██╔══██╗╚══██╔══╝██╔════╝
  ██║     ██████╔╝█████╗  ███████║   ██║   █████╗
  ██║     ██╔══██╗██╔══╝  ██╔══██║   ██║   ██╔══╝
  ╚██████╗██║  ██║███████╗██║  ██║   ██║   ███████╗
   ╚═════╝╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  // region create
  async create(
    accessDoorType: IAccessDoorType
  ): Promise<boolean> {
    let flag = false;
    const urlApi = `${environment.urlApi}/access-door-types`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Registrando tipo de puerta';

    try {
      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse>((resolve, reject) => {
        this.http.post<IServerResponse>(
          urlApi,
          JSON.stringify(accessDoorType),
          { headers: { 'content-type': 'application/json', 'Authorization': token.toString() } }
        ).subscribe({
          next: (response: IServerResponse) => resolve(new ServerResponse(response)),
          error: (error) => {
            if (error.error) { resolve(new ServerResponse(error.error)); }
            else {
              reject(error);
              this.toastService.show({
                header: 'Error de servidor',
                body: `${error}`,
                classname: 'bg-danger text-white'
              });
            }
          }
        });
      });

      if (response.statusCode === 201) {
        flag = true;
        this.toastService.show({
          body: `${response.message}`,
          classname: 'bg-success text-white'
        });
        this.getAll();
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }
    } catch (error) {
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';
    return flag;
  }

  /*
  ██████╗ ███████╗██╗     ███████╗████████╗███████╗
  ██╔══██╗██╔════╝██║     ██╔════╝╚══██╔══╝██╔════╝
  ██║  ██║█████╗  ██║     █████╗     ██║   █████╗
  ██║  ██║██╔══╝  ██║     ██╔══╝     ██║   ██╔══╝
  ██████╔╝███████╗███████╗███████╗   ██║   ███████╗
  ╚═════╝ ╚══════╝╚══════╝╚══════╝   ╚═╝   ╚══════╝

  */
  // region delete
  async delete(
    accessDoorType: AccessDoorType
  ): Promise<boolean> {
    let flag = false;
    const urlApi = `${environment.urlApi}/access-door-types/${accessDoorType.accessDoorType}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Eliminando tipo de puerta';

    try {
      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse>((resolve, reject) => {
        this.http.delete<IServerResponse>(
          urlApi,
          { headers: { 'Authorization': token.toString() } }
        ).subscribe({
          next: (response: IServerResponse) => resolve(new ServerResponse(response)),
          error: (error) => {
            if (error.error) { resolve(new ServerResponse(error.error)); }
            else {
              reject(error);
              this.toastService.show({
                header: 'Error de servidor',
                body: `${error}`,
                classname: 'bg-danger text-white'
              });
            }
          }
        });
      });

      if (response.statusCode === 200) {
        flag = true;
        this.toastService.show({
          body: `${response.message}`,
          classname: 'bg-success text-white'
        });
        this.getAll();
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }
    } catch (error) {
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';
    return flag;
  }

  /*
   ██████╗ ███████╗████████╗
  ██╔════╝ ██╔════╝╚══██╔══╝
  ██║  ███╗█████╗     ██║
  ██║   ██║██╔══╝     ██║
  ╚██████╔╝███████╗   ██║███████╗
   ╚═════╝ ╚══════╝   ╚═╝╚══════╝
  */
  async get(accessDoorType: AccessDoorType): Promise<AccessDoorType | null> {
    const urlApi = `${environment.urlApi}/access-door-types/${accessDoorType.accessDoorType}`;

    try {
      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<{ accessDoorType: IAccessDoorType }>>((resolve, reject) => {
        this.http.get<IServerResponse<{ accessDoorType: IAccessDoorType }>>(
          urlApi,
          { headers: { 'content-type': 'application/json', 'Authorization': token.toString() } }
        ).subscribe({
          next: (response) => resolve(new ServerResponse<{ accessDoorType: IAccessDoorType }>(response)),
          error: (error) => {
            if (error.error) { resolve(new ServerResponse(error.error)); }
            else { reject(error); }
          }
        });
      });

      if (response.statusCode === 200) {
        return new AccessDoorType(response.data!.accessDoorType);
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
        return null;
      }
    } catch (error) {
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
      return null;
    }
  }

  /*
   ██████╗ ███████╗████████╗      █████╗ ██╗     ██╗
  ██╔════╝ ██╔════╝╚══██╔══╝     ██╔══██╗██║     ██║
  ██║  ███╗█████╗     ██║        ███████║██║     ██║
  ██║   ██║██╔══╝     ██║        ██╔══██║██║     ██║
  ╚██████╔╝███████╗   ██║███████╗██║  ██║███████╗███████╗
   ╚═════╝ ╚══════╝   ╚═╝╚══════╝╚═╝  ╚═╝╚══════╝╚══════╝

  */
  // region getAll
  async getAll() {
    this.loading = true;
    const urlApi = `${environment.urlApi}/access-door-types`;

    try {
      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<{ accessDoorTypes: IAccessDoorType[] }>>((resolve, reject) => {
        this.http.get<IServerResponse<{ accessDoorTypes: IAccessDoorType[] }>>(
          urlApi,
          { headers: { 'content-type': 'application/json', 'Authorization': token.toString() } }
        ).subscribe({
          next: (response) => resolve(new ServerResponse<{ accessDoorTypes: IAccessDoorType[] }>(response)),
          error: (error) => {
            if (error.error) { resolve(new ServerResponse(error.error)); }
            else { reject(error); }
          }
        });
      });

      if (response.statusCode === 200) {
        this.accessDoorTypes = response.data!.accessDoorTypes.map(t => new AccessDoorType(t));
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }
    } catch (error) {
      console.log(error);
    }

    this.loading = false;
  }

  /*
  ██╗   ██╗██████╗ ██████╗  █████╗ ████████╗███████╗
  ██║   ██║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔════╝
  ██║   ██║██████╔╝██║  ██║███████║   ██║   █████╗
  ██║   ██║██╔═══╝ ██║  ██║██╔══██║   ██║   ██╔══╝
  ╚██████╔╝██║     ██████╔╝██║  ██║   ██║   ███████╗
   ╚═════╝ ╚═╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝
  */
  async update(
    oldType: string,
    accessDoorType: IAccessDoorType
  ): Promise<boolean> {
    let flag = false;
    const urlApi = `${environment.urlApi}/access-door-types/${oldType}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Actualizando tipo de puerta';

    try {
      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse>((resolve, reject) => {
        this.http.put<IServerResponse>(
          urlApi,
          JSON.stringify(accessDoorType),
          { headers: { 'content-type': 'application/json', 'Authorization': token.toString() } }
        ).subscribe({
          next: (response: IServerResponse) => resolve(new ServerResponse(response)),
          error: (error) => {
            if (error.error) { resolve(new ServerResponse(error.error)); }
            else {
              reject(error);
              this.toastService.show({
                header: 'Error de servidor',
                body: `${error}`,
                classname: 'bg-danger text-white'
              });
            }
          }
        });
      });

      if (response.statusCode === 200) {
        flag = true;
        this.toastService.show({
          body: `${response.message}`,
          classname: 'bg-success text-white'
        });
        this.getAll();
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }
    } catch (error) {
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';
    return flag;
  }
}
